import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Minus, Plus, ShoppingBag, Trash2, Sparkles, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatARS, API } from "@/lib/format";
import CheckoutSheet from "@/components/CheckoutSheet";


function CartItemThumb({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      onLoad={() => setLoaded(true)}
      className={`w-full h-full object-cover bg-black transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
    />
  );
}
export default function CartDrawer({ combos = [] }) {
  const { items, isOpen, setIsOpen, increment, decrement, remove, subtotal, addItem, setPromoMode } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const [evalData, setEvalData] = useState(null);
  const [evalStatus, setEvalStatus] = useState("idle"); // idle | loading | ok | error
  const [maxSug, setMaxSug] = useState(1);
  const [mensajeEnvio, setMensajeEnvio] = useState("");
  const [mensajeCarrito, setMensajeCarrito] = useState("");
  const evalSeq = useRef(0);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      const n = parseInt(r.data?.maxSugerencias);
      setMaxSug(Number.isFinite(n) ? n : 1);
      setMensajeEnvio(r.data?.mensajeEnvioCorto || "");
      setMensajeCarrito(r.data?.mensajeCarrito || "");
    }).catch(() => {});
  }, []);

  const runEval = (attempt = 0) => {
    if (!items.length) { setEvalData(null); setEvalStatus("idle"); return; }
    const seq = ++evalSeq.current;
    setEvalStatus("loading");
    axios.post(`${API}/promos/evaluar`, {
      items: items.map((i) => ({ productoId: i.productoId, tamano: i.tamano, cantidad: i.cantidad, personalizacion: i.personalizacion || null })),
    }, { timeout: 20000 })
      .then((r) => {
        if (seq !== evalSeq.current) return;
        setEvalData(r.data);
        setEvalStatus("ok");
      })
      .catch(() => {
        if (seq !== evalSeq.current) return;
        if (attempt < 2) setTimeout(() => runEval(attempt + 1), 700 * (attempt + 1));
        else setEvalStatus("error");
      });
  };

  useEffect(() => {
    const t = setTimeout(runEval, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (isOpen || checkoutOpen) runEval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, checkoutOpen]);

  const promoYaAplicada = (evalData?.promosAplicadas?.length || 0) > 0;

  const completarCombo = (sug) => {
    const targets = (sug.addItems || []).map((ai) => ({
      productoId: ai.productoId,
      categoria: ai.categoria || null,
      sabores: ai.sabores || [],
      tamano: ai.tamano || null,
      cantidad: ai.cantidadReq || ai.cantidad,
    }));
    setPromoMode({ promoId: sug.promoId, nombre: sug.nombre, targets });
    setIsOpen(false);
    toast(`Completando ${sug.nombre}`, { description: "Elegí los productos marcados — el resto queda bloqueado" });
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          onClick={() => setIsOpen(false)}
          data-testid="cart-drawer-overlay"
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-[#0F0F11] border-l border-line z-50 shadow-2xl transition-transform duration-300 flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-line">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-brand" />
            <h2 className="font-heading text-2xl uppercase text-white">Tu Pedido</h2>
          </div>
          <button
            data-testid="cart-close-button"
            onClick={() => setIsOpen(false)}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-5 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 rounded-full bg-panel border border-line flex items-center justify-center mb-4">
                <ShoppingBag className="w-6 h-6 text-zinc-500" />
              </div>
              <p className="text-zinc-500 text-sm uppercase tracking-widest">Tu carrito está vacío</p>
              <p className="text-zinc-600 text-xs mt-2">Agregá alguna promo para arrancar</p>
            </div>
          ) : (
            items.map((i) => (
              <div
                key={i.key}
                data-testid={`cart-item-${i.productoId}`}
                className="flex gap-3 p-3 bg-panel border border-line rounded-lg"
              >
                <div className="w-16 h-16 rounded overflow-hidden bg-black shrink-0">
                  <CartItemThumb src={i.imagen} alt={i.nombre} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white leading-tight line-clamp-2">{i.nombre}</div>
                  <div className="text-xs text-gold font-heading text-lg mt-0.5">{formatARS(i.precioBase ?? i.precioUnitario)}</div>
                  {i.extrasDetalle?.map((x, xi) => (
                    <div key={xi} data-testid={`cart-extra-${i.productoId}-${xi}`} className="text-[11px] text-zinc-400 mt-0.5">
                      + {x.label} <span className="text-gold font-bold">{formatARS(x.extra)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 bg-black rounded border border-line">
                      <button
                        data-testid={`cart-item-decrement-${i.productoId}`}
                        onClick={() => decrement(i.key)}
                        className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-white"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-white text-sm font-bold w-6 text-center">{i.cantidad}</span>
                      <button
                        data-testid={`cart-item-increment-${i.productoId}`}
                        onClick={() => increment(i.key)}
                        className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-white"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => remove(i.key)}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                      data-testid={`cart-item-remove-${i.productoId}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {!promoYaAplicada && evalData?.sugerencias?.length > 0 && (
            <div className="mt-4 space-y-2">
              {evalData.sugerencias.slice(0, maxSug).map((s) => (
                <div key={s.promoId} data-testid={`cart-promo-suggestion-${s.promoId}`} className="p-4 rounded-lg border border-gold/40 bg-gold/5">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <span className="text-xs uppercase tracking-widest font-black text-gold">Te falta poco para la promo</span>
                  </div>
                  <div className="text-sm font-bold text-white">{s.nombre} · {s.descuentoPorcentaje ? `-${s.descuentoPorcentaje}% OFF` : formatARS(s.precioCombo)}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{s.textoFaltantes} — ahorrás {formatARS(s.ahorroEstimado)}</div>
                  <button
                    data-testid={`cart-promo-completar-${s.promoId}`}
                    onClick={() => completarCombo(s)}
                    className="mt-2 bg-gold hover:bg-gold-strong text-black font-black uppercase text-[11px] px-3 py-2 rounded transition-colors"
                  >
                    Completar combo
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-line p-5 space-y-3">
          {mensajeEnvio && items.length > 0 && (
            <div data-testid="cart-envio-mensaje" className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold font-bold">
              <ShoppingBag className="w-3.5 h-3.5" /> {mensajeEnvio}
            </div>
          )}
          {evalData?.promosAplicadas?.map((a) => (
            <div key={a.promoId} data-testid={`cart-promo-applied-${a.promoId}`} className="flex items-center justify-between text-xs">
              <span className="uppercase tracking-widest font-black text-emerald-400 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Promo {a.nombre}{a.veces > 1 ? ` x${a.veces}` : ""}
              </span>
              <span className="text-emerald-400 font-bold">-{formatARS(a.ahorro)}</span>
            </div>
          ))}
          {evalData && evalData.ahorro > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className="uppercase tracking-widest text-zinc-500">Subtotal sin promo</span>
              <span className="text-zinc-500 line-through">{formatARS(evalData.totalSinPromo)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-zinc-500">Total</span>
            {evalStatus === "loading" ? (
              <span data-testid="cart-total-loading" className="font-heading text-xl text-zinc-400">Calculando...</span>
            ) : evalStatus === "error" ? (
              <button data-testid="cart-eval-retry" onClick={() => runEval()} className="text-xs uppercase tracking-widest font-bold text-red-400 hover:text-red-300 underline">
                Error — reintentar
              </button>
            ) : (
              <span data-testid="cart-subtotal-price" className="font-heading text-3xl text-gold">
                {formatARS(evalData ? evalData.total : subtotal)}
              </span>
            )}
          </div>
          {mensajeCarrito && items.length > 0 && (
            <p data-testid="cart-mensaje-carrito" className="text-xs text-gold font-bold text-center uppercase tracking-widest">{mensajeCarrito}</p>
          )}
          <button
            data-testid="cart-checkout-button"
            onClick={() => setCheckoutOpen(true)}
            disabled={items.length === 0 || evalStatus === "loading" || evalStatus === "error"}
            className="w-full bg-brand hover:bg-brand-strong disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-black uppercase tracking-widest py-3 rounded transition-colors duration-200"
          >
            {evalStatus === "loading" ? "Calculando total..." : "Continuar"}
          </button>
        </div>
      </aside>

      <CheckoutSheet open={checkoutOpen} onClose={() => setCheckoutOpen(false)} totalFinal={evalData?.total} evalStatus={evalStatus} />
    </>
  );
}
